import { PlusOutlined } from "@ant-design/icons";
import { useState } from "react";
import styled from "styled-components";

const StyledInputRow = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  width: 100%;
`;

const StyledAddValueButton = styled.button`
  padding: 4px 8px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;

  &:hover {
    border-color: #1890ff;
    color: #1890ff;

    .anticon {
      color: inherit;
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    border-color: #d9d9d9;
    color: #d9d9d9;
  }
`;

const StyledPlusIcon = styled(PlusOutlined)`
  font-size: 12px;
`;

/**
 * Base range field component providing shared behavior for numeric and date ranges.
 * Handles min/max or start/end range inputs with validation and partial range support.
 *
 * @param {object} props
 * @param {*} props.minValue - Current minimum/start value
 * @param {*} props.maxValue - Current maximum/end value
 * @param {function} props.onValueChange - Called when range changes with complete range object
 * @param {function} props.onAddValue - Called when user clicks add button
 * @param {string} props.minKey - Key name for minimum value (e.g., "min_value", "start")
 * @param {string} props.maxKey - Key name for maximum value (e.g., "max_value", "end")
 * @param {string} props.minLabel - Label for minimum input (e.g., "Min", "Start Date")
 * @param {string} props.maxLabel - Label for maximum input (e.g., "Max", "End Date")
 * @param {function} props.validateRange - Validation function (min, max) => boolean
 * @param {boolean} props.canAddValue - Whether the add button should be enabled
 * @param {function} props.renderMinInput - Render prop for minimum input component
 * @param {function} props.renderMaxInput - Render prop for maximum input component
 */
function UdfBaseRangeField({
  minValue,
  maxValue,
  onValueChange,
  onAddValue,
  minKey = "min_value",
  maxKey = "max_value",
  minLabel = "Min",
  maxLabel = "Max",
  validateRange,
  canAddValue,
  renderMinInput,
  renderMaxInput,
}) {
  const [localMin, setLocalMin] = useState(minValue);
  const [localMax, setLocalMax] = useState(maxValue);

  const handleMinChange = (value) => {
    setLocalMin(value);

    // Build range object based on what values are present
    if (value !== undefined && value !== null) {
      if (localMax !== undefined && localMax !== null) {
        // Both values present - validate range
        if (validateRange(value, localMax)) {
          onValueChange({ [minKey]: value, [maxKey]: localMax });
        } else {
          onValueChange(null);
        }
      } else {
        // Only min value
        onValueChange({ [minKey]: value, [maxKey]: null });
      }
    } else if (localMax !== undefined && localMax !== null) {
      // Only max value
      onValueChange({ [minKey]: null, [maxKey]: localMax });
    } else {
      onValueChange(null);
    }
  };

  const handleMaxChange = (value) => {
    setLocalMax(value);

    // Build range object based on what values are present
    if (value !== undefined && value !== null) {
      if (localMin !== undefined && localMin !== null) {
        // Both values present - validate range
        if (validateRange(localMin, value)) {
          onValueChange({ [minKey]: localMin, [maxKey]: value });
        } else {
          onValueChange(null);
        }
      } else {
        // Only max value
        onValueChange({ [minKey]: null, [maxKey]: value });
      }
    } else if (localMin !== undefined && localMin !== null) {
      // Only min value
      onValueChange({ [minKey]: localMin, [maxKey]: null });
    } else {
      onValueChange(null);
    }
  };

  const handleAdd = () => {
    onAddValue();
    setLocalMin(undefined);
    setLocalMax(undefined);
  };

  return (
    <StyledInputRow>
      {renderMinInput({
        value: localMin,
        onChange: handleMinChange,
        placeholder: minLabel,
      })}
      {renderMaxInput({
        value: localMax,
        onChange: handleMaxChange,
        placeholder: maxLabel,
      })}
      <StyledAddValueButton
        onClick={handleAdd}
        disabled={!canAddValue}
        aria-label={`Add ${minLabel.toLowerCase()}-${maxLabel.toLowerCase()} range`}
      >
        <StyledPlusIcon />
      </StyledAddValueButton>
    </StyledInputRow>
  );
}

export default UdfBaseRangeField;
